<template>
  <div class="game-trade-view">
    <div class="shop-window">
      <div class="shop-chrome shop-chrome-top"></div>

      <header class="shop-header">
        <div class="shop-title-block">
          <p class="shop-kicker">{{ activeKicker }}</p>
          <h1>Trading Post</h1>
        </div>
        <div class="shop-gold">
          <span class="gold-label">Gold</span>
          <span class="gold-value">{{ formatNumber(playerGold) }}</span>
        </div>
      </header>

      <nav class="shop-tabs" aria-label="Trading Post sections">
        <button
          type="button"
          class="shop-tab"
          :class="{ active: activeTab === 'exchange' }"
          @click="openExchange"
        >
          Licensed Commodity Exchange
        </button>
        <button
          type="button"
          class="shop-tab"
          :class="{ active: activeTab === 'cards' }"
          @click="openCardMarket"
        >
          Card Market
        </button>
        <button
          type="button"
          class="shop-tab"
          :class="{ active: activeTab === 'mine' }"
          @click="openMyMarket"
        >
          My Listings / Offers
        </button>
      </nav>

      <template v-if="activeTab === 'exchange'">
        <p class="shop-subtitle">
          Five houses keep the realm's stalls. Prices rise as stock thins.
        </p>

        <div v-if="loading" class="shop-status">Consulting the ledgers…</div>
        <div v-else-if="loadError" class="shop-status is-error">{{ loadError }}</div>

        <div v-else class="shop-list" role="list">
          <div
            v-for="listing in listings"
            :key="listing.slug"
            class="shop-row"
            :class="{ selected: selectedSlug === listing.slug }"
            role="listitem"
            @click="selectRow(listing.slug)"
          >
            <span class="row-cursor" aria-hidden="true">▶</span>

            <span class="row-identity">
              <span class="row-company">{{ listing.company }}</span>
              <span class="row-resource">
                <i
                  :class="resourceDisplay(listing.slug).icon"
                  :style="{ color: resourceDisplay(listing.slug).color }"
                ></i>
                {{ listing.name }}
              </span>
            </span>

            <span class="row-prices">
              <span class="price-block">
                <span class="price-label">Buy</span>
                <span class="price-value">{{ formatNumber(listing.buy_price) }}</span>
              </span>
              <span class="price-block">
                <span class="price-label">Sell</span>
                <span class="price-value">{{ formatNumber(listing.sell_price) }}</span>
              </span>
            </span>

            <span class="row-stock">
              <span class="stock-you">You {{ formatNumber(playerStock(listing.slug)) }}</span>
              <span class="stock-stall">Stall {{ formatNumber(listing.current_inventory) }}</span>
            </span>

            <span class="row-actions" @click.stop>
              <input
                :id="`qty-${listing.slug}`"
                v-model.number="quantities[listing.slug]"
                class="qty-input"
                type="number"
                min="1"
                max="9999"
                @focus="selectRow(listing.slug)"
              />
              <button
                type="button"
                class="shop-btn buy"
                :disabled="!canBuy(listing) || busy"
                @click="requestTrade('buy', listing)"
              >
                Buy
              </button>
              <button
                type="button"
                class="shop-btn sell"
                :disabled="!canSell(listing) || busy"
                @click="requestTrade('sell', listing)"
              >
                Sell
              </button>
            </span>
          </div>
        </div>

        <footer class="shop-help">
          <p v-if="flashMessage" :class="['help-flash', flashKind]">{{ flashMessage }}</p>
          <p v-else-if="selectedListing" class="help-line">
            <span class="help-cmd">Buy</span>
            {{ selectedQty }} {{ selectedListing.name }} —
            {{ formatNumber(selectedBuyTotal) }} gold
            <span class="help-sep">/</span>
            <span class="help-cmd">Sell</span>
            {{ formatNumber(selectedSellTotal) }} gold
          </p>
          <p v-else class="help-line">Select a house to trade.</p>
        </footer>
      </template>

      <template v-else-if="activeTab === 'cards'">
        <p class="shop-subtitle">
          Players list cards for gold. Instant buy pays the ask; offers hold your gold until
          the seller answers. Listings expire after 24 hours.
        </p>

        <div class="filter-bar">
          <input
            v-model="searchText"
            class="filter-search"
            type="search"
            placeholder="Search by card name…"
          />
          <select v-model="rarityFilter">
            <option value="">All rarities</option>
            <option v-for="rarity in rarityOptions" :key="rarity" :value="rarity">
              {{ rarity }}
            </option>
          </select>
          <select v-model="typeFilter">
            <option value="">All types</option>
            <option v-for="typeName in typeOptions" :key="typeName" :value="typeName">
              {{ typeName }}
            </option>
          </select>
        </div>

        <div v-if="cardLoading && !cardLoaded" class="shop-status">Unrolling the card ledgers…</div>
        <div v-else-if="cardLoadError && !cardLoaded" class="shop-status is-error">{{ cardLoadError }}</div>

        <div v-else class="card-market">
          <h2 class="market-section-title">Active Listings</h2>
          <p v-if="cardListings.length === 0" class="empty-market">
            {{
              hasCardFilters
                ? "No cards match these filters."
                : "No cards are listed right now."
            }}
          </p>
          <div v-else class="shop-list card-list" role="list">
            <div
              v-for="listing in cardListings"
              :key="listing.listing_id"
              class="shop-row card-row"
              :class="{ selected: selectedListingId === listing.listing_id }"
              role="listitem"
              @click="openListingDrawer(listing)"
            >
              <div class="card-preview">
                <PlayingCard
                  v-if="listing.card"
                  :card="listing.card"
                  :face-up="true"
                  :art-only="true"
                />
              </div>
              <span class="row-identity">
                <span class="row-company">{{ listing.card?.name || "Unknown card" }}</span>
                <span class="row-resource">
                  {{ listing.card?.rarity }} · {{ listing.seller_nickname || "Unknown" }}
                </span>
              </span>
              <span class="row-prices">
                <span class="price-block">
                  <span class="price-label">Ask</span>
                  <span class="price-value">{{ formatNumber(listing.price) }}</span>
                </span>
                <span class="price-block">
                  <span class="price-label">Time</span>
                  <span class="price-value">{{ formatRemaining(listing.expires_at) }}</span>
                </span>
              </span>
              <span class="row-actions" @click.stop>
                <button
                  type="button"
                  class="shop-btn buy"
                  :disabled="isOwnListing(listing) || !canAfford(listing.price) || busy"
                  @click="requestCardBuy(listing)"
                >
                  Buy
                </button>
                <button
                  type="button"
                  class="shop-btn sell"
                  :disabled="isOwnListing(listing) || busy"
                  @click="openListingDrawer(listing)"
                >
                  Offer
                </button>
              </span>
            </div>
          </div>

          <div v-if="showCardPagination" class="market-pagination">
            <button
              type="button"
              class="shop-btn"
              :disabled="cardPage <= 1 || busy"
              @click="goToCardPage(cardPage - 1)"
            >
              Prev
            </button>
            <span class="page-indicator">Page {{ cardPage }} of {{ cardTotalPages }}</span>
            <button
              type="button"
              class="shop-btn"
              :disabled="cardPage >= cardTotalPages || busy"
              @click="goToCardPage(cardPage + 1)"
            >
              Next
            </button>
          </div>
        </div>

        <footer class="shop-help">
          <p v-if="flashMessage" :class="['help-flash', flashKind]">{{ flashMessage }}</p>
          <p v-else class="help-line">
            List a card from your collection. Offers lock gold until accepted, declined, or the listing ends.
          </p>
        </footer>
      </template>

      <template v-else-if="activeTab === 'mine'">
        <p class="shop-subtitle">
          Manage cards you have listed and gold you have held on other players' asks.
        </p>

        <div v-if="mineLoading && !mineLoaded" class="shop-status">
          Unrolling your ledgers…
        </div>
        <div v-else-if="mineLoadError && !mineLoaded" class="shop-status is-error">
          {{ mineLoadError }}
        </div>
        <div v-else class="card-market">
          <h2 class="market-section-title">My Listings</h2>
          <p v-if="myListings.length === 0" class="empty-market">
            You have no active listings.
          </p>
          <div
            v-for="listing in myListings"
            :key="`mine-${listing.listing_id}`"
            class="mine-block"
          >
            <div class="mine-header">
              <span>{{ listing.card?.name }} · {{ formatNumber(listing.price) }} gold</span>
              <span>{{ formatRemaining(listing.expires_at) }} left</span>
              <button
                type="button"
                class="shop-btn"
                :disabled="busy"
                @click="requestCancelListing(listing)"
              >
                Cancel
              </button>
            </div>
            <p v-if="!(listing.offers || []).length" class="mine-empty">No pending offers.</p>
            <div
              v-for="offer in listing.offers || []"
              :key="offer.offer_id"
              class="offer-row"
            >
              <span>
                {{ offer.buyer_nickname || "Buyer" }} offers
                {{ formatNumber(offer.offer_price) }} gold
                (you receive {{ formatNumber(sellerNet(offer.offer_price)) }} after tax)
              </span>
              <span class="row-actions">
                <button
                  type="button"
                  class="shop-btn buy"
                  :disabled="busy"
                  @click="requestResolveOffer(listing, offer, 'accept')"
                >
                  Accept
                </button>
                <button
                  type="button"
                  class="shop-btn"
                  :disabled="busy"
                  @click="requestResolveOffer(listing, offer, 'reject')"
                >
                  Reject
                </button>
              </span>
            </div>
          </div>

          <h2 class="market-section-title">Your held offers</h2>
          <p v-if="myOffers.length === 0" class="empty-market">
            You have no gold held on other listings.
          </p>
          <p
            v-for="offer in myOffers"
            :key="`out-${offer.offer_id}`"
            class="outgoing-line"
          >
            {{ formatNumber(offer.offer_price) }} gold held on
            {{ offer.card?.name || "a card" }}
            until {{ formatRemaining(offer.expires_at) }} remain on the listing.
          </p>
        </div>

        <footer class="shop-help">
          <p v-if="flashMessage" :class="['help-flash', flashKind]">{{ flashMessage }}</p>
          <p v-else class="help-line">
            Accepting an offer pays you the bid minus the 3% success tax. Cancelling a listing does not refund the listing fee.
          </p>
        </footer>
      </template>

      <div class="shop-chrome shop-chrome-bottom"></div>
    </div>

    <Modal
      :is-visible="showModal"
      title="Confirm Trade"
      @confirm="confirmTrade"
      @cancel="cancelTrade"
    >
      <div v-if="pendingTrade" class="trade-confirm-body">
        <i
          class="trade-confirm-icon"
          :class="getResourceIcon(pendingTrade.slug)"
          :style="{
            color: `var(--rarity-color, ${resourceDisplay(pendingTrade.slug).color})`,
          }"
        ></i>
        <p>
          Are you sure you want to {{ pendingTrade.side }}
          {{ formatNumber(pendingTrade.quantity) }}
          {{ pendingTrade.name }} for
          {{ formatNumber(pendingTrade.gold) }} gold?
        </p>
      </div>
    </Modal>

    <Modal
      :is-visible="cardModalVisible"
      :title="cardModalTitle"
      :confirm-label="cardModalConfirmLabel"
      @confirm="confirmCardModal"
      @cancel="closeCardModal"
    >
      <div v-if="cardModal" class="trade-confirm-body card-modal-body">
        <p>{{ cardModalCopy }}</p>
      </div>
    </Modal>

    <div
      v-if="drawerListing"
      class="drawer-backdrop"
      @click.self="closeDrawer"
    ></div>
    <aside
      v-if="drawerListing"
      class="card-drawer"
      :class="{ 'materialize-anim': shouldAnimate }"
      @animationend="handleDrawerAnimationEnd"
    >
      <div v-if="shouldAnimate" class="dust-particles">
        <div
          class="particle"
          v-for="n in 20"
          :key="n"
          :style="getParticleStyle(n)"
        ></div>
      </div>
      <div class="card-drawer-panel glass-panel">
        <header class="drawer-header">
          <h2>{{ drawerListing.card?.name || "Listing" }}</h2>
          <button
            type="button"
            class="drawer-close"
            title="Close"
            @click="closeDrawer"
          >
            <i class="fas fa-times"></i>
          </button>
        </header>
        <div class="drawer-card-wrap">
          <PlayingCard
            v-if="drawerListing.card"
            class="drawer-card"
            :card="drawerListing.card"
            :face-up="true"
          />
          <dl class="drawer-meta">
            <div>
              <dt>Seller</dt>
              <dd>{{ drawerListing.seller_nickname || "Unknown" }}</dd>
            </div>
            <div>
              <dt>Ask Price</dt>
              <dd>{{ formatNumber(drawerListing.price) }} gold</dd>
            </div>
            <div>
              <dt>Time Remaining</dt>
              <dd>{{ formatRemaining(drawerListing.expires_at) }}</dd>
            </div>
            <div v-if="pendingOffer">
              <dt>Your Offer</dt>
              <dd>{{ formatNumber(pendingOffer.offer_price) }} gold held</dd>
            </div>
          </dl>
          <div v-if="!isOwnListing(drawerListing) && !pendingOffer" class="drawer-offer">
            <label class="offer-input-label">
              Offer gold
              <input
                v-model.number="offerAmount"
                class="qty-input offer-input"
                type="number"
                min="1"
                step="1"
              />
            </label>
            <button
              type="button"
              class="shop-btn buy drawer-submit"
              :disabled="!canSubmitOffer || busy"
              @click="submitOffer"
            >
              Submit Offer
            </button>
          </div>
          <p v-else-if="isOwnListing(drawerListing)" class="drawer-hint">
            This is your listing.
          </p>
        </div>
      </div>
    </aside>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, reactive, ref, watch } from "vue";
import { useGameStore } from "../store/game";
import { useMarketStore } from "../store/market";
import { useAuthStore } from "../store/auth";
import { useUiStore } from "../store/ui";
import { getResourceIcon, resourceDisplay } from "../utils/resourceDisplay";
import {
  previewBuyCost,
  previewSellProceeds,
} from "../utils/marketPricing";
import { sellerNet } from "../utils/cardMarketFees";
import Modal from "../components/Modal.vue";
import PlayingCard from "../components/PlayingCard.vue";

const RARITY_OPTIONS = ["common", "uncommon", "rare", "epic", "legendary"];
const TYPE_OPTIONS = ["building", "hero", "equipment"];

const gameStore = useGameStore();
const marketStore = useMarketStore();
const authStore = useAuthStore();
const uiStore = useUiStore();

const activeTab = ref("exchange");
const loading = ref(true);
const loadError = ref("");
const cardLoading = ref(false);
const cardLoaded = ref(false);
const mineLoading = ref(false);
const mineLoaded = ref(false);
const mineLoadError = ref("");
const busy = ref(false);
const selectedSlug = ref("");
const selectedListingId = ref(null);
const flashMessage = ref("");
const flashKind = ref("ok");
const quantities = reactive({});
const showModal = ref(false);
const pendingTrade = ref(null);
const cardModal = ref(null);
const offerAmount = ref(1);
const drawerListing = ref(null);
const shouldAnimate = ref(false);
const searchText = ref("");
const rarityFilter = ref("");
const typeFilter = ref("");
let searchTimer = null;

const listings = computed(() => gameStore.marketPrices || []);
const playerGold = computed(() => Number(gameStore.spendableResources.gold) || 0);
const myUserId = computed(() => Number(authStore.user?.id) || 0);
const cardListings = computed(() => marketStore.cardListings || []);
const myListings = computed(() => marketStore.myListings || []);
const myOffers = computed(() => marketStore.myOffers || []);
const cardLoadError = computed(() => marketStore.loadError || "");
const cardPage = computed(() => marketStore.cardPage || 1);
const cardTotalPages = computed(() => marketStore.cardTotalPages || 1);
const rarityOptions = RARITY_OPTIONS;
const typeOptions = TYPE_OPTIONS;
const hasCardFilters = computed(
  () =>
    Boolean(searchText.value.trim()) ||
    Boolean(rarityFilter.value) ||
    Boolean(typeFilter.value)
);
const showCardPagination = computed(
  () => cardListings.value.length > 0 || cardPage.value > 1 || cardTotalPages.value > 1
);
const pendingOffer = computed(() => {
  const listingId = Number(drawerListing.value?.listing_id);
  if (!listingId) return null;
  return (
    myOffers.value.find((offer) => Number(offer.listing_id) === listingId) || null
  );
});
const canSubmitOffer = computed(() => {
  const listing = drawerListing.value;
  if (!listing || isOwnListing(listing) || pendingOffer.value) return false;
  return canAfford(normalizeQty(offerAmount.value));
});

const activeKicker = computed(() => {
  if (activeTab.value === "cards") return "Player Card Market";
  if (activeTab.value === "mine") return "Your Card Listings";
  return "Licensed Commodity Exchange";
});

const selectedListing = computed(() =>
  listings.value.find((row) => row.slug === selectedSlug.value) || null
);

const selectedQty = computed(() => {
  if (!selectedListing.value) return 1;
  return normalizeQty(quantities[selectedListing.value.slug]);
});

const selectedBuyTotal = computed(() => {
  if (!selectedListing.value) return 0;
  return previewBuyCost(selectedListing.value, selectedQty.value) ?? 0;
});

const selectedSellTotal = computed(() => {
  if (!selectedListing.value) return 0;
  return previewSellProceeds(selectedListing.value, selectedQty.value) ?? 0;
});

const cardModalVisible = computed(() => !!cardModal.value);
const cardModalTitle = computed(() => {
  const kind = cardModal.value?.kind;
  if (kind === "buy") return "Buy Card";
  if (kind === "accept") return "Accept Offer";
  if (kind === "reject") return "Reject Offer";
  if (kind === "cancel") return "Cancel Listing";
  return "Card Market";
});
const cardModalConfirmLabel = computed(() => {
  const kind = cardModal.value?.kind;
  if (kind === "buy") return "Buy";
  if (kind === "accept") return "Accept";
  if (kind === "reject") return "Reject";
  if (kind === "cancel") return "Cancel Listing";
  return "Confirm";
});
const cardModalCopy = computed(() => {
  const modal = cardModal.value;
  if (!modal) return "";
  const name = modal.listing?.card?.name || "this card";
  if (modal.kind === "buy") {
    return `Pay ${formatNumber(modal.listing.price)} gold for ${name}?`;
  }
  if (modal.kind === "accept") {
    const gross = Number(modal.offer.offer_price) || 0;
    return `Accept ${formatNumber(gross)} gold for ${name}? You receive ${formatNumber(sellerNet(gross))} after the 3% success tax.`;
  }
  if (modal.kind === "reject") {
    return `Decline this offer of ${formatNumber(modal.offer.offer_price)} gold? The buyer’s gold will be returned.`;
  }
  if (modal.kind === "cancel") {
    return `Cancel your listing for ${name}? The 2% listing fee is not refunded. Pending offers will be returned.`;
  }
  return "";
});

function formatNumber(value) {
  return new Intl.NumberFormat().format(Number(value) || 0);
}

function formatRemaining(expiresAt) {
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (!Number.isFinite(ms) || ms <= 0) return "Expired";
  const totalMin = Math.max(0, Math.floor(ms / 60000));
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h <= 0) return `${m}m`;
  return `${h}h ${m}m`;
}

function normalizeQty(value) {
  const n = Number.parseInt(value, 10);
  if (!Number.isInteger(n) || n < 1) return 1;
  return Math.min(n, 9999);
}

function playerStock(slug) {
  return Number(gameStore.spendableResources[slug]) || 0;
}

function canBuy(listing) {
  const qty = normalizeQty(quantities[listing.slug]);
  const cost = previewBuyCost(listing, qty);
  if (cost == null) return false;
  return playerGold.value >= cost;
}

function canSell(listing) {
  const qty = normalizeQty(quantities[listing.slug]);
  return playerStock(listing.slug) >= qty;
}

function canAfford(amount) {
  return playerGold.value >= (Number(amount) || 0);
}

function isOwnListing(listing) {
  return Number(listing?.seller_id) === myUserId.value;
}

function selectRow(slug) {
  selectedSlug.value = slug;
}

function seedQuantities(prices) {
  for (const listing of prices) {
    if (quantities[listing.slug] == null) {
      quantities[listing.slug] = 1;
    }
  }
}

function requestTrade(side, listing) {
  const qty = normalizeQty(quantities[listing.slug]);
  quantities[listing.slug] = qty;
  selectedSlug.value = listing.slug;
  const gold =
    side === "buy"
      ? previewBuyCost(listing, qty)
      : previewSellProceeds(listing, qty);
  if (gold == null) return;
  pendingTrade.value = {
    side,
    slug: listing.slug,
    name: listing.name,
    quantity: qty,
    gold,
  };
  showModal.value = true;
}

function cancelTrade() {
  if (busy.value) return;
  showModal.value = false;
  pendingTrade.value = null;
}

async function confirmTrade() {
  const pending = pendingTrade.value;
  if (!pending || busy.value) return;
  await executeTrade(pending);
}

async function executeTrade(pending) {
  busy.value = true;
  flashMessage.value = "";
  try {
    const action =
      pending.side === "buy" ? gameStore.marketBuy : gameStore.marketSell;
    const result = await action({
      slug: pending.slug,
      quantity: pending.quantity,
    });
    const gold = Number(result?.trade?.gold) || 0;
    const verb = pending.side === "buy" ? "Purchased" : "Sold";
    flashKind.value = "ok";
    flashMessage.value = `${verb} ${pending.quantity} ${pending.name} for ${formatNumber(gold)} gold.`;
    showModal.value = false;
    pendingTrade.value = null;
  } catch (error) {
    flashKind.value = "err";
    flashMessage.value =
      error.response?.data?.error ||
      error.response?.data?.message ||
      error.message ||
      "The trade could not be completed.";
    showModal.value = false;
    pendingTrade.value = null;
  } finally {
    busy.value = false;
  }
}

async function loadCardListings(page = 1) {
  const isFirst = !cardLoaded.value;
  if (isFirst) cardLoading.value = true;
  try {
    await marketStore.fetchCardListings({
      page,
      search: searchText.value.trim(),
      rarity: rarityFilter.value,
      type: typeFilter.value,
    });
    cardLoaded.value = true;
  } catch {
    if (cardLoaded.value) {
      flashKind.value = "err";
      flashMessage.value =
        marketStore.loadError || "Could not load the card market.";
    }
  } finally {
    if (isFirst) cardLoading.value = false;
  }
}

function openExchange() {
  closeDrawer();
  activeTab.value = "exchange";
}

async function openCardMarket() {
  if (activeTab.value !== "cards") closeDrawer();
  activeTab.value = "cards";
  if (cardLoaded.value || cardLoading.value) return;
  try {
    await Promise.all([loadCardListings(1), marketStore.fetchMyCardMarket()]);
    mineLoaded.value = true;
  } catch {
    /* loadError is stored on the market store */
  }
}

async function openMyMarket() {
  closeDrawer();
  activeTab.value = "mine";
  if (mineLoading.value) return;
  const isFirst = !mineLoaded.value;
  if (isFirst) mineLoading.value = true;
  mineLoadError.value = "";
  try {
    await marketStore.fetchMyCardMarket();
    mineLoaded.value = true;
  } catch (error) {
    mineLoadError.value =
      error.response?.data?.message || "Could not load your listings.";
    if (mineLoaded.value) {
      flashKind.value = "err";
      flashMessage.value = mineLoadError.value;
    }
  } finally {
    if (isFirst) mineLoading.value = false;
  }
}

function goToCardPage(page) {
  const next = Math.max(1, Math.min(page, cardTotalPages.value));
  loadCardListings(next);
}

function openListingDrawer(listing) {
  selectedListingId.value = listing.listing_id;
  offerAmount.value = Math.max(1, Number(listing.price) || 1);
  drawerListing.value = listing;
}

function closeDrawer() {
  shouldAnimate.value = false;
  drawerListing.value = null;
}

function handleDrawerAnimationEnd() {
  shouldAnimate.value = false;
}

const MathRand = (min, max) => Math.random() * (max - min) + min;
function getParticleStyle() {
  return {
    left: `${MathRand(0, 100)}%`,
    top: `${MathRand(0, 100)}%`,
    animationDelay: `${MathRand(0, 0.5)}s`,
    animationDuration: `${MathRand(1, 2)}s`,
    transform: `scale(${MathRand(0.5, 1.5)})`,
  };
}

watch(
  drawerListing,
  (listing, prev) => {
    uiStore.setSidePanelOpen(!!listing);
    if (!listing) {
      shouldAnimate.value = false;
      return;
    }
    if (!prev) {
      shouldAnimate.value = true;
    }
  },
  { immediate: true }
);

watch(searchText, () => {
  if (activeTab.value !== "cards") return;
  clearTimeout(searchTimer);
  searchTimer = setTimeout(() => {
    loadCardListings(1);
  }, 300);
});

watch([rarityFilter, typeFilter], () => {
  if (activeTab.value !== "cards") return;
  loadCardListings(1);
});

function requestCardBuy(listing) {
  selectedListingId.value = listing.listing_id;
  cardModal.value = { kind: "buy", listing };
}

function requestResolveOffer(listing, offer, action) {
  cardModal.value = { kind: action, listing, offer };
}

function requestCancelListing(listing) {
  cardModal.value = { kind: "cancel", listing };
}

function closeCardModal() {
  if (busy.value) return;
  cardModal.value = null;
}

async function submitOffer() {
  const listing = drawerListing.value;
  if (!listing || busy.value || !canSubmitOffer.value) return;
  const amount = normalizeQty(offerAmount.value);
  if (!canAfford(amount)) {
    flashKind.value = "err";
    flashMessage.value = "Not enough gold to hold this offer.";
    return;
  }
  busy.value = true;
  flashMessage.value = "";
  try {
    await marketStore.makeOffer({
      listingId: listing.listing_id,
      offerPrice: amount,
    });
    flashKind.value = "ok";
    flashMessage.value = `${formatNumber(amount)} gold is held on ${listing.card?.name}.`;
  } catch (error) {
    flashKind.value = "err";
    flashMessage.value =
      error.response?.data?.error ||
      error.response?.data?.message ||
      error.message ||
      "The card market could not complete that action.";
  } finally {
    busy.value = false;
  }
}

async function confirmCardModal() {
  const modal = cardModal.value;
  if (!modal || busy.value) return;
  busy.value = true;
  flashMessage.value = "";
  try {
    if (modal.kind === "buy") {
      const result = await marketStore.buyCard(modal.listing.listing_id);
      flashKind.value = "ok";
      flashMessage.value = `Purchased ${modal.listing.card?.name} for ${formatNumber(result?.trade?.price)} gold.`;
      if (
        Number(drawerListing.value?.listing_id) === Number(modal.listing.listing_id)
      ) {
        closeDrawer();
      }
    } else if (modal.kind === "accept" || modal.kind === "reject") {
      const result = await marketStore.resolveOffer({
        offerId: modal.offer.offer_id,
        action: modal.kind,
      });
      flashKind.value = "ok";
      flashMessage.value =
        modal.kind === "accept"
          ? `Accepted ${formatNumber(result?.trade?.price)} gold (${formatNumber(result?.trade?.seller_net)} after tax).`
          : "Offer declined. Gold returned to the buyer.";
    } else if (modal.kind === "cancel") {
      await marketStore.cancelListing(modal.listing.listing_id);
      flashKind.value = "ok";
      flashMessage.value = `${modal.listing.card?.name} returned to your collection.`;
    }
    cardModal.value = null;
  } catch (error) {
    flashKind.value = "err";
    flashMessage.value =
      error.response?.data?.error ||
      error.response?.data?.message ||
      error.message ||
      "The card market could not complete that action.";
    cardModal.value = null;
  } finally {
    busy.value = false;
  }
}

onMounted(async () => {
  loading.value = true;
  loadError.value = "";
  try {
    const prices = await gameStore.fetchMarketPrices();
    seedQuantities(prices);
    if (prices.length) {
      selectedSlug.value = prices[0].slug;
    }
  } catch (error) {
    loadError.value =
      error.response?.data?.message ||
      "The trading post could not be reached.";
  } finally {
    loading.value = false;
  }
});

onUnmounted(() => {
  clearTimeout(searchTimer);
  shouldAnimate.value = false;
  uiStore.setSidePanelOpen(false);
});
</script>

<style scoped>
@import url("https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.4/css/all.min.css");
@import url("https://fonts.googleapis.com/css2?family=Cinzel:wght@600;700&display=swap");

.game-trade-view {
  height: 100%;
  overflow-y: auto;
  padding: 1.25rem 1.5rem 1.5rem;
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

.shop-window {
  position: relative;
  width: min(1080px, 100%);
  background: rgba(8, 12, 28, 0.82);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(232, 213, 163, 0.85);
  box-shadow:
    0 0 0 1px rgba(8, 12, 28, 0.95),
    0 0 0 3px rgba(232, 213, 163, 0.35),
    0 18px 40px rgba(0, 0, 0, 0.45);
  color: #f4f1e4;
  padding: 1.1rem 1.25rem 0.85rem;
}

.shop-chrome {
  position: absolute;
  left: 8px;
  right: 8px;
  height: 2px;
  background: rgba(232, 213, 163, 0.55);
  pointer-events: none;
}

.shop-chrome-top {
  top: 8px;
}

.shop-chrome-bottom {
  bottom: 8px;
}

.shop-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 1rem;
  margin-top: 0.35rem;
}

.shop-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin: 0.85rem 0 0.15rem;
}

.shop-tab {
  font-family: "Cinzel", "Palatino Linotype", Palatino, serif;
  font-size: 0.72rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 0.38rem 0.75rem;
  background: transparent;
  color: #c9c3b0;
  border: 1px solid rgba(232, 213, 163, 0.35);
  cursor: pointer;
}

.shop-tab.active,
.shop-tab:hover {
  color: #fff8e4;
  border-color: rgba(232, 213, 163, 0.85);
  background: rgba(232, 213, 163, 0.12);
}

.shop-kicker {
  margin: 0 0 0.2rem;
  font-size: 0.72rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: #c8b56a;
  font-family: "Exo 2", sans-serif;
}

h1 {
  margin: 0;
  font-family: "Cinzel", "Palatino Linotype", Palatino, serif;
  font-size: 1.85rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  text-shadow: 0 2px 0 #000, 0 0 12px rgba(232, 213, 163, 0.35);
  color: #fff8e4;
}

.shop-gold {
  min-width: 9rem;
  text-align: right;
  border: 1px solid rgba(232, 213, 163, 0.55);
  background: rgba(0, 0, 0, 0.35);
  padding: 0.45rem 0.75rem;
}

.gold-label {
  display: block;
  font-size: 0.7rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #c8b56a;
}

.gold-value {
  font-family: "Exo 2", sans-serif;
  font-size: 1.35rem;
  color: #ffd76a;
}

.shop-subtitle {
  margin: 0.55rem 0 0.9rem;
  color: #c9c3b0;
  font-size: 0.9rem;
}

.shop-status {
  padding: 2rem 1rem;
  text-align: center;
  color: #d8d2c2;
  font-family: "Cinzel", "Palatino Linotype", Palatino, serif;
}

.shop-status.is-error {
  color: #f0a0a0;
}

.shop-list {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  border-top: 1px solid rgba(232, 213, 163, 0.35);
  border-bottom: 1px solid rgba(232, 213, 163, 0.35);
  padding: 0.45rem 0;
}

.shop-row {
  display: grid;
  grid-template-columns: 1.4rem minmax(12rem, 1.4fr) minmax(8rem, 0.9fr) minmax(8rem, 0.8fr) auto;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  box-sizing: border-box;
  text-align: left;
  background: transparent;
  border: 1px solid transparent;
  color: #efe9d8;
  padding: 0.55rem 0.55rem;
  font-family: "Exo 2", sans-serif;
  cursor: pointer;
}

.shop-row:hover {
  background: rgba(232, 213, 163, 0.08);
  color: #fff8e4;
  box-shadow: none;
}

.shop-row.selected {
  background: rgba(232, 213, 163, 0.16);
  border-color: rgba(232, 213, 163, 0.7);
  color: #e8d5a3;
  box-shadow: none;
}

.row-cursor {
  opacity: 0;
  color: #e8d5a3;
  font-size: 0.8rem;
}

.shop-row.selected .row-cursor {
  opacity: 1;
}

.row-identity {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.row-company {
  font-family: "Cinzel", "Palatino Linotype", Palatino, serif;
  font-size: 0.98rem;
  letter-spacing: 0.03em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.row-resource {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.78rem;
  color: #c9c3b0;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.row-prices,
.row-stock {
  display: flex;
  gap: 0.85rem;
  font-variant-numeric: tabular-nums;
}

.price-block,
.row-stock span {
  display: flex;
  flex-direction: column;
}

.price-label,
.stock-you,
.stock-stall {
  font-size: 0.65rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #a8a090;
}

.price-value,
.stock-you,
.stock-stall {
  font-size: 0.95rem;
  color: #fff8e4;
}

.stock-you,
.stock-stall {
  font-size: 0.82rem;
  letter-spacing: 0.04em;
  text-transform: none;
}

.row-actions {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  justify-content: flex-end;
}

.qty-input {
  width: 4.2rem;
  background: rgba(0, 0, 0, 0.45);
  border: 1px solid rgba(232, 213, 163, 0.55);
  color: #fff8e4;
  font-family: "Exo 2", sans-serif;
  font-size: 0.95rem;
  padding: 0.35rem 0.4rem;
  text-align: right;
}

.qty-input:focus {
  outline: 1px solid #e8d5a3;
}

.shop-btn {
  font-family: "Cinzel", "Palatino Linotype", Palatino, serif;
  font-size: 0.78rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 0.38rem 0.7rem;
  border-radius: 0;
  background: rgba(0, 0, 0, 0.35);
  color: #e8d5a3;
  border: 1px solid rgba(232, 213, 163, 0.75);
  box-shadow: none;
}

.shop-btn:hover:not(:disabled) {
  background: rgba(232, 213, 163, 0.2);
  color: #fff8e4;
  box-shadow: none;
}

.shop-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
  color: #9a9178;
  border-color: rgba(154, 145, 120, 0.5);
}

.shop-help {
  min-height: 2.4rem;
  margin-top: 0.7rem;
  margin-bottom: 0.35rem;
  font-size: 0.88rem;
  color: #d8d2c2;
}

.help-line,
.help-flash {
  margin: 0;
}

.help-cmd {
  color: #e8d5a3;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-size: 0.78rem;
}

.help-sep {
  margin: 0 0.55rem;
  color: #7d7664;
}

.help-flash.ok {
  color: #d4e8a8;
}

.help-flash.err {
  color: #f0a0a0;
}

.trade-confirm-body {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
}

.trade-confirm-icon {
  font-size: 1.6rem;
  line-height: 1.6;
  flex-shrink: 0;
}

.trade-confirm-body p {
  margin: 0;
}

.card-list .card-row {
  grid-template-columns: 3.6rem minmax(10rem, 1.4fr) minmax(8rem, 0.9fr) auto;
  align-items: stretch;
}

.card-row .row-identity,
.card-row .row-prices,
.card-row .row-actions {
  align-self: center;
}

.card-preview {
  width: 3.6rem;
  align-self: stretch;
  min-height: 0;
  overflow: hidden;
  pointer-events: none;
}

.card-preview :deep(.playing-card) {
  width: 100%;
  height: 100%;
}

.filter-bar {
  display: grid;
  grid-template-columns: 1.6fr repeat(2, minmax(110px, 1fr));
  gap: 0.55rem;
  margin: 0 0 0.85rem;
}

.filter-search,
.filter-bar select {
  background: rgba(0, 0, 0, 0.45);
  border: 1px solid rgba(232, 213, 163, 0.55);
  color: #fff8e4;
  padding: 8px 10px;
  font: inherit;
}

.filter-search:focus,
.filter-bar select:focus {
  outline: 1px solid #e8d5a3;
}

.market-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin: 0.7rem 0 0.35rem;
}

.page-indicator {
  font-size: 0.82rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #c8b56a;
}

.market-section-title {
  margin: 1rem 0 0.45rem;
  font-family: "Cinzel", "Palatino Linotype", Palatino, serif;
  font-size: 0.95rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #e8d5a3;
}

.market-section-title.is-sub {
  font-size: 0.8rem;
}

.empty-market,
.mine-empty {
  margin: 0 0 0.7rem;
  color: #a8a090;
  font-size: 0.88rem;
}

.mine-block {
  border: 1px solid rgba(232, 213, 163, 0.25);
  padding: 0.55rem 0.65rem;
  margin-bottom: 0.55rem;
}

.mine-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.55rem 0.9rem;
  margin-bottom: 0.4rem;
  color: #fff8e4;
}

.offer-row,
.outgoing-line {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.45rem;
  padding: 0.35rem 0;
  color: #d8d2c2;
  font-size: 0.88rem;
}

.offer-input-label {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-top: 0.65rem;
  font-size: 0.82rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #c8b56a;
}

.offer-input {
  width: 8rem;
}

.drawer-offer .offer-input-label {
  margin-top: 0;
}

.drawer-submit {
  margin-top: 0.55rem;
  align-self: flex-start;
}

.drawer-hint {
  margin: 0;
  color: #a8a090;
  font-size: 0.88rem;
}

.materialize-anim {
  animation: panel-materialize 1.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
  opacity: 0;
}

@keyframes panel-materialize {
  0% {
    opacity: 0;
    transform: translateX(50px) scale(0.95);
    filter: blur(10px) brightness(2);
  }
  50% {
    opacity: 0.8;
    filter: blur(2px) brightness(1.5);
  }
  100% {
    opacity: 1;
    transform: translateX(0) scale(1);
    filter: blur(0) brightness(1);
  }
}

.dust-particles {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 900;
  overflow: hidden;
}

.particle {
  position: absolute;
  width: 4px;
  height: 4px;
  background-color: #e8d5a3;
  border-radius: 50%;
  box-shadow: 0 0 8px #e8d5a3, 0 0 12px #c8b56a;
  opacity: 0;
  animation: particle-fade-float 2s ease-out forwards;
}

@keyframes particle-fade-float {
  0% {
    opacity: 0;
    transform: translateY(20px) scale(1);
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform: translateY(-40px) scale(0);
  }
}

.card-drawer {
  position: fixed;
  right: 0;
  top: 70px;
  bottom: 80px;
  width: 350px;
  z-index: 800;
  display: flex;
  flex-direction: column;
}

.glass-panel {
  flex-grow: 1;
  background-color: rgba(20, 20, 30, 0.85);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-left: 1px solid rgba(232, 213, 163, 0.35);
  box-shadow: -5px 0 20px rgba(0, 0, 0, 0.5), inset 0 0 20px rgba(232, 213, 163, 0.05);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: 100%;
}

.drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.9rem 1rem;
  border-bottom: 1px solid rgba(232, 213, 163, 0.25);
  background: rgba(36, 40, 59, 0.6);
}

.drawer-header h2 {
  margin: 0;
  font-size: 1rem;
  color: #e8d5a3;
  font-family: "Cinzel", "Palatino Linotype", Palatino, serif;
}

.drawer-close {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  padding: 0;
  margin: 0;
  line-height: 1;
  background: none;
  border: 1px solid rgba(232, 213, 163, 0.35);
  color: #a0a0b0;
  border-radius: 6px;
  cursor: pointer;
}

.drawer-close:hover {
  color: #e8d5a3;
  border-color: #e8d5a3;
}

.drawer-card-wrap {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.75rem;
  padding: 0.75rem 1rem 1.1rem;
  min-height: 0;
  overflow-y: auto;
}

.drawer-card {
  width: 100%;
  max-width: 100%;
  flex-shrink: 0;
}

.drawer-meta {
  margin: 0;
  display: grid;
  gap: 0.45rem;
}

.drawer-meta div {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  font-size: 0.88rem;
}

.drawer-meta dt {
  margin: 0;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-size: 0.7rem;
  color: #c8b56a;
}

.drawer-meta dd {
  margin: 0;
  color: #fff8e4;
  text-align: right;
}

.drawer-offer {
  display: flex;
  flex-direction: column;
}

@media (max-width: 800px) {
  .filter-bar {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 900px) {
  .card-list .card-row {
    grid-template-columns: 3.4rem 1fr;
    grid-template-areas: none;
  }

  .shop-row:not(.card-row) {
    grid-template-columns: 1.2rem 1fr;
    grid-template-areas:
      "cursor identity"
      "cursor prices"
      "cursor stock"
      "cursor actions";
  }

  .shop-row:not(.card-row) .row-cursor {
    grid-area: cursor;
  }
  .shop-row:not(.card-row) .row-identity {
    grid-area: identity;
  }
  .shop-row:not(.card-row) .row-prices {
    grid-area: prices;
  }
  .shop-row:not(.card-row) .row-stock {
    grid-area: stock;
  }
  .shop-row:not(.card-row) .row-actions {
    grid-area: actions;
    justify-content: flex-start;
  }
}

.drawer-backdrop {
  display: none;
}

@media (max-width: 768px) {
  .drawer-backdrop {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(3px);
    -webkit-backdrop-filter: blur(3px);
    z-index: 1190;
  }

  .card-drawer {
    top: 0;
    right: 0;
    bottom: 0;
    width: 86vw;
    max-width: 380px;
    height: 100dvh;
    z-index: 1200;
    box-shadow: -8px 0 24px rgba(0, 0, 0, 0.7);
    transform: translateX(0);
    transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  }

  .materialize-anim {
    animation: none;
    opacity: 1;
  }

  .card-drawer-panel.glass-panel {
    padding-bottom: env(safe-area-inset-bottom, 0px);
  }
}
</style>
